import { z } from 'zod/v4';
import { parseMarkdown, type MarkdownNode } from './markdown';

export type MarkdownReviewPart = { id: string; start: number; end: number; before: string };
export type MarkdownSuggestion = MarkdownReviewPart & { after: string; reason: string };
const replacement = z.object({ id: z.string().max(80), after: z.string().min(1).max(1200), reason: z.string().max(300) }).strict();
export const markdownReviewOutput = z.object({ suggestions: z.array(replacement).max(24) }).strict();
const unsafe = /[\r\n\u0000-\u001f\u007f*_~`<>\[\]\\{}|$#!]/u;

/** Only literal prose leaves are reviewable. Syntax, URLs and complex blocks never enter the model. */
export function markdownReviewParts(source: string): MarkdownReviewPart[] {
  const parsed = parseMarkdown(source), parts: MarkdownReviewPart[] = [];
  if (parsed.issues.some(issue => ['frontmatter', 'extension', 'mixed-endings', 'math'].includes(issue.code))) return parts;
  const shift = parsed.bom ? 1 : 0;
  const lines = source.split(/\r\n|\r|\n/);
  function visit(node: MarkdownNode): void {
    if (['code','inlineCode','html','table','link','linkReference','image','imageReference','definition','footnoteDefinition'].includes(node.type)) return;
    if (node.type === 'listItem' && node.checked != null) return;
    if (node.type === 'text' && node.position && /[\p{L}\p{N}]/u.test(node.value) && node.value.length <= 1200) {
      const start = node.position.start.offset, end = node.position.end.offset;
      if (start === undefined || end === undefined || node.position.start.line !== node.position.end.line ||
        (lines[node.position.start.line - 1]?.length ?? 0) > 1600) return;
      const before = source.slice(start + shift, end + shift);
      if (before !== node.value || unsafe.test(before)) return;
      parts.push({ id: 'prose-' + (start + shift) + '-' + (end + shift), start: start + shift, end: end + shift, before });
    } else if ('children' in node) node.children.forEach(visit);
  }
  parsed.tree.children.forEach(visit);
  return parts.slice(0, 24);
}
function shape(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(shape);
  if (value && typeof value === 'object') return Object.fromEntries(Object.entries(value).filter(([key]) => key !== 'position')
    .map(([key, child]) => [key, key === 'value' && 'type' in value && value.type === 'text' ? '' : shape(child)]));
  return value;
}
export function validateMarkdownSuggestions(source: string, raw: unknown): MarkdownSuggestion[] {
  const output = markdownReviewOutput.parse(raw), parts = markdownReviewParts(source), ids = new Set<string>();
  const suggestions = output.suggestions.map(item => {
    const part = parts.find(part => part.id === item.id);
    if (!part || ids.has(item.id) || unsafe.test(item.after) || item.after === part.before ||
      item.after.match(/^\s*/)?.[0] !== part.before.match(/^\s*/)?.[0] ||
      item.after.match(/\s*$/)?.[0] !== part.before.match(/\s*$/)?.[0]) throw new Error('Review contains an invalid prose change.');
    ids.add(item.id);
    return { ...part, ...item };
  });
  const next = splice(source, suggestions);
  if (JSON.stringify(shape(parseMarkdown(source).tree)) !== JSON.stringify(shape(parseMarkdown(next).tree)))
    throw new Error('Review would change protected Markdown syntax.');
  return suggestions;
}
function splice(source: string, suggestions: readonly MarkdownSuggestion[]) {
  let next = source;
  for (const item of [...suggestions].sort((a,b) => b.start - a.start)) next = next.slice(0,item.start) + item.after + next.slice(item.end);
  return next;
}
/** Revalidate the exact original, offsets and every selected edit at the point of explicit Apply. */
export function applyMarkdownSuggestions(current: string, reviewed: string, suggestions: readonly MarkdownSuggestion[], selected: readonly string[]): string {
  if (current !== reviewed || new Set(selected).size !== selected.length) throw new Error('The draft changed. Review it again.');
  const checked = validateMarkdownSuggestions(reviewed, { suggestions: suggestions.map(({id,after,reason}) => ({id,after,reason})) });
  if (JSON.stringify(checked) !== JSON.stringify(suggestions) || selected.some(id => !checked.some(item => item.id === id))) throw new Error('Review no longer matches its source.');
  const chosen = checked.filter(item => selected.includes(item.id));
  // Subsets must also preserve syntax; applying one suggestion cannot rely on another.
  const valid = validateMarkdownSuggestions(reviewed, { suggestions: chosen.map(({id,after,reason}) => ({id,after,reason})) });
  return splice(reviewed, valid);
}
